import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import '../components/AdminLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PaygPricing {
  id: string;
  featureKey: string;
  pricePerUnit: number;
  priceInr: number | null;
  maxPerMonth: number | null;
  updatedAt: string;
}

const PAYG_FEATURES: Array<{ key: string; label: string }> = [
  { key: 'slideshow', label: 'Slideshow' },
  { key: 'shorts', label: 'Shorts' },
  { key: 'subtitle', label: 'Subtitle' },
  { key: 'youtube_posts', label: 'YouTube Posts' },
  { key: 'kinetic_caption', label: 'Kinetic Caption' },
  { key: 'y_ray', label: 'Y-Ray' },
  { key: 'tiktok_posts', label: 'TikTok Posts' },
];

const AdminPricingPayg: React.FC = () => {
  const [pricing, setPricing] = useState<PaygPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, { pricePerUnit: string; priceInr: string; maxPerMonth: string }>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/pricing/payg`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const incoming: PaygPricing[] = data.pricing || [];
        const byFeature = new Map(incoming.map((item) => [item.featureKey, item]));
        const normalized = PAYG_FEATURES.map((feature) => byFeature.get(feature.key) || {
          id: feature.key,
          featureKey: feature.key,
          pricePerUnit: 0,
          priceInr: null,
          maxPerMonth: null,
          updatedAt: new Date().toISOString(),
        });
        setPricing(normalized);
        const initial: typeof edits = {};
        normalized.forEach((p: PaygPricing) => {
          initial[p.featureKey] = {
            pricePerUnit: String(p.pricePerUnit),
            priceInr: p.priceInr != null ? String(p.priceInr) : '',
            maxPerMonth: p.maxPerMonth != null ? String(p.maxPerMonth) : '',
          };
        });
        setEdits(initial);
      })
      .catch(() => setError('Failed to load pricing'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (featureKey: string) => {
    const edit = edits[featureKey];
    if (!edit) return;
    const priceInr = edit.priceInr.trim() ? Number(edit.priceInr) : null;
    if (priceInr !== null && (!Number.isFinite(priceInr) || priceInr <= 0)) {
      setError('India price must be a positive number (leave blank for none)');
      return;
    }
    setSaving(featureKey);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/pricing/payg`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          featureKey,
          pricePerUnit: parseFloat(edit.pricePerUnit),
          priceInr,
          maxPerMonth: edit.maxPerMonth ? parseInt(edit.maxPerMonth, 10) : null,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated: PaygPricing = await res.json();
      setPricing(prev => prev.map(p => p.featureKey === featureKey ? updated : p));
    } catch {
      setError(`Failed to save ${featureKey}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back">
        <div className="admin-page-title-row">
          <AdminBackButton />
          <h1 className="admin-page-title">Pay-as-you-go Pricing</h1>
        </div>
        <p className="admin-page-subtitle">Set price per unit and monthly caps for each feature.</p>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Feature Key</th>
                <th>Price / Unit ($)</th>
                <th>Price / Unit for India (₹)</th>
                <th>Max / Month</th>
                <th>Last Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pricing.length === 0 && (
                <tr><td colSpan={6} className="admin-cell-empty">No pricing entries yet.</td></tr>
              )}
              {pricing.map(p => (
                <tr key={p.featureKey}>
                  <td className="admin-cell-primary">
                    {PAYG_FEATURES.find((feature) => feature.key === p.featureKey)?.label || p.featureKey}
                    <div className="admin-cell-mono">{p.featureKey}</div>
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input-sm"
                      type="number"
                      step="0.0001"
                      min="0"
                      value={edits[p.featureKey]?.pricePerUnit ?? ''}
                      onChange={e => setEdits(prev => ({
                        ...prev,
                        [p.featureKey]: { ...prev[p.featureKey], pricePerUnit: e.target.value },
                      }))}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input-sm"
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder="Blank = no India price"
                      value={edits[p.featureKey]?.priceInr ?? ''}
                      onChange={e => setEdits(prev => ({
                        ...prev,
                        [p.featureKey]: { ...prev[p.featureKey], priceInr: e.target.value },
                      }))}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input-sm"
                      type="number"
                      min="0"
                      placeholder="Unlimited"
                      value={edits[p.featureKey]?.maxPerMonth ?? ''}
                      onChange={e => setEdits(prev => ({
                        ...prev,
                        [p.featureKey]: { ...prev[p.featureKey], maxPerMonth: e.target.value },
                      }))}
                    />
                  </td>
                  <td className="admin-cell-muted">
                    {new Date(p.updatedAt).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-primary"
                      disabled={saving === p.featureKey}
                      onClick={() => handleSave(p.featureKey)}
                    >
                      {saving === p.featureKey ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPricingPayg;
